import { createPortal } from 'react-dom'
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { Hash, MessagesSquare, Search, Volume2, X } from 'lucide-react'
import { avatarUrl, guildIconUrl } from '@/lib/avatar'
import { cx } from '@/lib/cx'
import { guildColor, guildInitials, isUnread, plural } from '@/lib/guildUi'
import { DM_SETTINGS, channelMuted, ownMuted } from '@/lib/muting'
import { P, channelPermissions, has } from '@/lib/permissions'
import { groupPlaces, indexPlaces, searchPlaces, type Place } from '@/lib/quickSwitch'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { useHotkeyStore } from '@/store/hotkeys'
import { channelName, displayName, useSocialStore } from '@/store/social'
import { ChannelType, type Member } from '@/types/api'
import { Avatar } from './Avatar'
import { GroupAvatar } from './GroupAvatar'
import styles from './QuickSwitcher.module.css'

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

/** Доступные серверы, каналы, беседы и друзья. */
function collectPlaces(g: ReturnType<typeof useGuildStore.getState>, soc: ReturnType<typeof useSocialStore.getState>, me: ReturnType<typeof useAppStore.getState>['currentUser']): Place[] {
  const out: Place[] = []
  for (const guild of Object.values(g.guilds)) {
    out.push({ key: `g:${guild.id}`, kind: 'guild', name: guild.name, guildId: guild.id, image: guildIconUrl(guild.id, guild.icon), imageId: guild.id })
    if (!me) continue
    const self: Member = g.members[guild.id]?.[me.id] ?? {
      user: { id: me.id, username: me.username, global_name: me.global_name, avatar: null },
      nick: null, avatar: null, roles: [], joined_at: '', premium_since: null, deaf: false, mute: false, pending: false, communication_disabled_until: null,
    }
    const channels = g.channels[guild.id] ?? {}
    const settings = g.settings[guild.id]
    for (const ch of Object.values(channels)) {
      const voice = ch.type === ChannelType.GuildVoice
      const forum = ch.type === ChannelType.GuildForum
      if ((!voice && !forum && ch.type !== ChannelType.GuildText && ch.type !== ChannelType.GuildAnnouncement) || !has(channelPermissions(guild, self, ch), P.VIEW_CHANNEL)) continue
      const rs = soc.readStates[ch.id]
      out.push({
        key: `c:${ch.id}`,
        kind: voice ? 'voice' : forum ? 'forum' : 'text',
        name: ch.name ?? '',
        sub: guild.name,
        guildId: guild.id,
        channelId: ch.id,
        mentions: rs?.mention_count ?? 0,
        unread: !voice && isUnread(ch, rs) && !channelMuted(settings, ch.id, channels),
      })
    }
  }
  const withDM = new Set<string>()
  const dmSettings = g.settings[DM_SETTINGS]
  for (const ch of Object.values(soc.channels)) {
    if (ch.type !== ChannelType.DM && ch.type !== ChannelType.GroupDM) continue
    // заглушённая беседа без счётчика — как в списке слева
    const rs = ownMuted(dmSettings, ch.id) ? undefined : soc.readStates[ch.id]
    if (ch.type === ChannelType.DM) {
      const r = ch.recipients?.[0]
      if (r) withDM.add(r.id)
      out.push({ key: `d:${ch.id}`, kind: 'dm', name: channelName(ch), sub: r ? `@${r.username}` : undefined, terms: r ? [r.username] : [], channelId: ch.id, mentions: rs?.mention_count ?? 0, image: r ? avatarUrl(r.id, r.avatar) : undefined, imageId: r?.id, decoration: r?.avatar_decoration_data })
    } else {
      const n = (ch.recipients?.length ?? 0) + 1
      out.push({ key: `d:${ch.id}`, kind: 'group', name: channelName(ch), sub: `${n} ${plural(n, 'участник', 'участника', 'участников')}`, terms: (ch.recipients ?? []).flatMap((u) => [u.username, displayName(u)]), channelId: ch.id, mentions: rs?.mention_count ?? 0, recipients: ch.recipients })
    }
  }
  // друзья, с кем беседы ещё нет: переход откроет её
  for (const rel of Object.values(soc.relationships)) {
    if (rel.type !== 1 || withDM.has(rel.user.id)) continue
    out.push({ key: `f:${rel.user.id}`, kind: 'friend', name: displayName(rel.user), sub: `@${rel.user.username}`, terms: [rel.user.username], userId: rel.user.id, image: avatarUrl(rel.user.id, rel.user.avatar), imageId: rel.user.id, decoration: rel.user.avatar_decoration_data })
  }
  return out
}

/** Быстрый переход (Ctrl/⌘+K): серверы, каналы, беседы и друзья; стрелки, Enter, Esc, мышь. */
export function QuickSwitcher() {
  const open = useHotkeyStore((s) => s.switcherOpen)
  if (!open) return null
  return <SwitcherModal />
}

function SwitcherModal() {
  const setOpen = useHotkeyStore((s) => s.setSwitcherOpen)
  const recent = useHotkeyStore((s) => s.recent)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [error, setError] = useState('')
  const [opening, setOpening] = useState(false)
  const pending = useRef(false)
  const mounted = useRef(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const g = useGuildStore()
  const soc = useSocialStore()
  const me = useAppStore((s) => s.currentUser)
  const listRef = useRef<HTMLDivElement | null>(null)
  const listId = useId()
  // Изменение прав или удаление канала сразу убирает его из поиска.
  const index = useMemo(() => indexPlaces(collectPlaces(g, soc, me)), [g, soc, me])
  const results = useMemo(() => searchPlaces(index, query, recent, 50), [index, query, recent])
  const groups = useMemo(() => groupPlaces(results, query, recent), [results, query, recent])
  const current = Math.min(active, Math.max(0, results.length - 1))

  const close = () => setOpen(false)

  useLayoutEffect(() => {
    mounted.current = true
    const previous = document.activeElement as HTMLElement | null
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    inputRef.current?.focus()
    const keepFocus = (e: FocusEvent) => {
      if (e.target instanceof Node && !cardRef.current?.contains(e.target)) inputRef.current?.focus()
    }
    document.addEventListener('focusin', keepFocus)
    return () => {
      mounted.current = false
      document.body.style.overflow = overflow
      document.removeEventListener('focusin', keepFocus)
      if (previous?.isConnected) previous.focus({ preventScroll: true })
    }
  }, [])

  // Esc закрывает только это окно: настройки или окно под ним остаются. Слушаем на window
  // в фазе захвата — раньше обработчиков окон и настроек, которые висят на document
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.isComposing) return
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [setOpen])

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${current}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [current, results])

  const go = async (place: Place | undefined) => {
    if (!place || pending.current) return
    const app = useAppStore.getState()
    const p = collectPlaces(useGuildStore.getState(), useSocialStore.getState(), app.currentUser).find((item) => item.key === place.key)
    if (!p) return
    if (p.kind === 'friend') {
      pending.current = true
      setOpening(true)
      setError('')
      const userId = app.currentUser?.id
      try {
        const id = await useSocialStore.getState().openDM(p.userId!)
        if (!mounted.current || useAppStore.getState().currentUser?.id !== userId) return
        setOpen(false)
        useAppStore.getState().setView({ kind: 'home', channelId: id })
        useAppStore.getState().showContent()
      } catch {
        if (mounted.current) setError('Не удалось открыть беседу. Попробуйте ещё раз.')
      } finally {
        pending.current = false
        if (mounted.current) setOpening(false)
      }
      return
    }
    setOpen(false)
    if (p.kind === 'guild') app.openGuild(p.guildId!)
    else if (p.kind === 'text' || p.kind === 'forum' || p.kind === 'voice') app.openGuild(p.guildId!, p.channelId!)
    else app.setView({ kind: 'home', channelId: p.channelId! })
    app.showContent()
  }

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive(results.length ? (current + 1) % results.length : 0)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(results.length ? (current - 1 + results.length) % results.length : 0)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      void go(results[current])
    }
  }

  return createPortal(
    <div className={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) close() }}>
      <div className={styles.card} ref={cardRef} role="dialog" aria-modal="true" aria-label="Найти или начать беседу" onKeyDown={(e) => {
        if (e.key !== 'Tab') return
        e.preventDefault()
        const target = e.target === inputRef.current ? cardRef.current?.querySelector<HTMLButtonElement>('button') : inputRef.current
        target?.focus()
      }}>
        <div className={styles.header}>
          <h2>Найти или начать беседу</h2>
          <button type="button" className={styles.close} onClick={close} aria-label="Закрыть" data-tooltip="Закрыть"><X size={20} /></button>
        </div>
        <div className={styles.field}>
          <Search size={20} className={styles.fieldIcon} />
          <input
            className={styles.input}
            ref={inputRef}
            value={query}
            placeholder="Куда перейти?"
            aria-label="Куда перейти"
            role="combobox"
            aria-expanded="true"
            aria-autocomplete="list"
            aria-controls={listId}
            aria-activedescendant={results.length ? `${listId}-${current}` : undefined}
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => {
              setError('')
              setQuery(e.target.value)
              setActive(0)
            }}
            onKeyDown={onKeyDown}
          />
        </div>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        {opening ? <p className={styles.status} role="status">Открываем беседу…</p> : null}
        <div className={cx(styles.list, 'scroller')} id={listId} role="listbox" ref={listRef} aria-label="Результаты">
          {groups.map((group) => (
            <div key={group.label} role="group" aria-label={group.label}>
              <div className={styles.section}>{group.label}</div>
              {group.places.map((p) => {
                const i = results.indexOf(p)
                return (
                  <div
                    key={p.key}
                    id={`${listId}-${i}`}
                    data-index={i}
                    role="option"
                    aria-selected={i === current}
                    className={cx(styles.item, i === current && styles.itemActive)}
                    onMouseMove={() => { if (i !== current) setActive(i) }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void go(p)}
                  >
                    <PlaceIcon place={p} />
                    <span className={cx(styles.name, p.unread && styles.unread)}>{p.name}</span>
                    {p.sub ? <span className={styles.sub}>{p.sub}</span> : null}
                    {p.mentions ? <span className={styles.badge}>{p.mentions}</span> : null}
                  </div>
                )
              })}
            </div>
          ))}
          {results.length === 0 ? <p className={styles.empty}>{query.trim() ? 'Ничего не нашлось' : 'Начните вводить название сервера, канала или имя'}</p> : null}
        </div>
        <div className={styles.footer}>
          Сузить поиск: <kbd>@</kbd> люди, <kbd>#</kbd> каналы и форумы, <kbd>!</kbd> голосовые, <kbd>*</kbd> серверы
        </div>
      </div>
    </div>,
    document.fullscreenElement ?? document.body,
  )
}

function PlaceIcon({ place }: { place: Place }) {
  switch (place.kind) {
    case 'text':
      return <Hash size={18} className={styles.icon} />
    case 'forum':
      return <MessagesSquare size={18} className={styles.icon} />
    case 'voice':
      return <Volume2 size={18} className={styles.icon} />
    case 'group':
      return <GroupAvatar recipients={place.recipients} size={24} />
    case 'guild':
      return (
        <span className={styles.guildIcon} style={place.image ? undefined : { background: guildColor(place.imageId ?? '0') }} aria-hidden="true">
          {place.image ? <img src={place.image} alt="" /> : guildInitials(place.name)}
        </span>
      )
    default:
      return <Avatar initial={place.name.slice(0, 1).toUpperCase()} color={colorOf(place.imageId ?? place.key)} size={24} src={place.image} decoration={place.decoration} />
  }
}
